import React, { useEffect, useRef } from 'react';
// @ts-ignore - progressbar.js doesn't have TypeScript definitions
import ProgressBar from 'progressbar.js';
import { formatTime } from '../utils/Formatting';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import './CircularTimer.css';

interface CircularTimerProps {
  timeRemaining: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const CircularTimer: React.FC<CircularTimerProps> = ({ timeRemaining, size = 60, strokeWidth = 4, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<ProgressBar.Circle | null>(null);
  const { settings } = useSettingsRedux();

  useEffect(() => {
    if (!containerRef.current || !settings?.timeLimitInSeconds) return;

    // Clean up previous progress bar
    if (progressBarRef.current) {
      progressBarRef.current.destroy();
    }

    // Create new progress bar
    progressBarRef.current = new ProgressBar.Circle(containerRef.current, {
      strokeWidth: strokeWidth,
      easing: 'easeInOut',
      duration: 1,
      color: 'var(--timer-progress-color)',
      trailColor: '#f0f0f0',
      trailWidth: strokeWidth,

      svgStyle: {
        width: `${size}px`,
        height: `${size}px`,
        transform: 'rotate(0deg) scaleX(-1)',
      },
      text: {
        style: {
          color: '#333',
          position: 'absolute',
          left: '50%',
          top: '50%',
          padding: 0,
          margin: 0,
          transform: 'translate(-50%, -50%)',
          fontSize: '0.75rem',
          fontWeight: '700',
          fontFamily: 'var(--main-body-font)',
        },
        autoStyleContainer: false,
      },
      step: (state: any, circle: any) => {
        circle.setText(formatTime(timeRemaining));
      },
    });

    // Calculate progress (1.0 means full circle, 0.0 means empty)
    const progress = (settings.timeLimitInSeconds > 0 ? timeRemaining / settings.timeLimitInSeconds : 0) * 0.95;

    progressBarRef.current.animate(progress);

    return () => {
      if (progressBarRef.current) {
        progressBarRef.current.destroy();
        progressBarRef.current = null;
      }
    };
  }, [timeRemaining, settings?.timeLimitInSeconds, size, strokeWidth]);

  return (
    <div
      ref={containerRef}
      className={`circular-timer ${className}`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        display: 'inline-block',
      }}
    />
  );
};

export default CircularTimer;
